import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { useMyAssistant } from "@/hooks/useMyAssistant";
import { usePlanUi } from "@/hooks/usePlanUi";
import { useLanguage } from "@/i18n/LanguageContext";
import { assistantPose } from "@/lib/assistants";
import { AssistantAvatar } from "@/components/assistant/AssistantPicker";
import { supabase } from "@/integrations/supabase/client";
import MarkerHighlight from "@/components/MarkerHighlight";

export const START_WELCOME_TOUR_EVENT = "rl:start-welcome-tour";
export type WelcomePlan = "lite" | "basic" | "pro" | "advanced";
const SEEN_KEY = "rl_welcome_tour_seen_v1";
const PENDING_KEY = "rl_welcome_tour_pending_test";
let pendingTestPlan: WelcomePlan | null = null;

/** Normaliza la clave de plan del catálogo a los cuatro recorridos. */
export function welcomePlanFor(planKey: string | null | undefined): WelcomePlan {
  const k = (planKey ?? "").toLowerCase();
  if (k.includes("advanced") || k.includes("avanzado") || k.includes("complet")) return "advanced";
  if (k.includes("pro") || k.includes("club")) return "pro";
  if (k.includes("basic") || k.includes("basico")) return "basic";
  return "lite";
}

type Step = { sel: string; t: string; b: string };

const QUOTA: Record<WelcomePlan, number> = { lite: 3, basic: 30, pro: 60, advanced: 100 };
const ADS: Record<WelcomePlan, number> = { lite: 0, basic: 0, pro: 4, advanced: 12 };

function quotaLine(es: boolean, plan: WelcomePlan, remaining: number | null) {
  const total = QUOTA[plan];
  const left = remaining ?? total;
  const ads = ADS[plan];
  if (es) {
    const q = left <= 0
      ? `Ahora mismo te quedan 0 consultas: verás «0 disponibles» y podrás ampliarlas mejorando tu plan.`
      : `Verás cuántas consultas te quedan este mes (${left} de ${total}).`;
    const a = ads > 0 ? ` También las imágenes para anuncios que te quedan (${ads} al mes).` : " La generación de imágenes para anuncios llega con Club Pro.";
    return `Arriba, junto a tu plan, siempre ves tu contador. ${q}${a} Pulsa «Mejora tu plan» para ver qué ganas con cada plan.`;
  }
  const q = left <= 0
    ? `Right now you have 0 queries left: you'll see "0 available" and can get more by upgrading.`
    : `You'll see how many queries you have left this month (${left} of ${total}).`;
  const a = ads > 0 ? ` Also your remaining ad images (${ads} a month).` : " Ad image generation comes with Club Pro.";
  return `Up top, next to your plan, you always see your counter. ${q}${a} Tap «Upgrade» to see what each plan adds.`;
}

const PLAN_SHORT: Record<"es" | "en", Record<WelcomePlan, string>> = {
  es: {
    lite: "Con Lite ves todas las herramientas; las que llevan candado se abren al mejorar tu plan.",
    basic: "Con Básico tienes calculadora, diagnóstico, detector, guías y listado de subvenciones abiertos.",
    pro: "Con Club Pro se suman tu hoja de ruta, KPIs, subvenciones que encajan contigo y casos reales completos.",
    advanced: "Con Avanzado tienes todas las herramientas, módulos premium y acceso opcional a abogados, licencias y proveedores.",
  },
  en: {
    lite: "With Lite you see every tool; locked ones open when you upgrade.",
    basic: "With Basic the calculator, diagnosis, detector, guides and grants list are open.",
    pro: "With Club Pro you add your roadmap, KPIs, matching grants and full real cases.",
    advanced: "With Advanced you get every tool, premium modules and optional access to lawyers, licences and suppliers.",
  },
};

function buildSteps(es: boolean, plan: WelcomePlan, name: string, remaining: number | null): Step[] {
  const L = es ? "es" : "en";
  return es ? [
    { sel: '[data-tour="sara-input"]', t: "1. Tu Agente Retail IA", b: `Aquí me escribes lo que necesites, como se lo contarías a un amigo. Elige el modo de respuesta y pulsa «Enviar». Soy ${name} y estoy aquí cada día.` },
    { sel: '[data-tour-guide="true"]', t: "2. El interrogante «?»", b: "En cualquier página, si no sabes qué es algo o cómo rellenarlo, pulsa aquí: te lo explico con un recorrido paso a paso." },
    { sel: '[data-tour="sidebar-account"]', t: "3. Tu perfil", b: "Arriba del menú lateral configuras tu perfil: datos del negocio, locales y el asistente que te acompaña." },
    { sel: '[data-tour="sidebar-nav"]', t: "4. Tus herramientas, en el menú lateral", b: "En el menú lateral están las herramientas por las que puedes navegar. " + PLAN_SHORT[L][plan] },
    { sel: '[data-tour="plan-pill"]', t: "5. Tus consultas y tu plan", b: quotaLine(true, plan, remaining) },
  ] : [
    { sel: '[data-tour="sara-input"]', t: "1. Your Retail AI Agent", b: `Write whatever you need here, as you'd tell a friend. Pick the answer mode and press «Send». I'm ${name} and I'm here every day.` },
    { sel: '[data-tour-guide="true"]', t: "2. The «?» button", b: "On any page, if you're unsure what something is or how to fill it in, tap here for a step-by-step walkthrough." },
    { sel: '[data-tour="sidebar-account"]', t: "3. Your profile", b: "At the top of the sidebar you set up your profile: business details, locations and your assistant." },
    { sel: '[data-tour="sidebar-nav"]', t: "4. Your tools, in the sidebar", b: "The sidebar holds the tools you can navigate. " + PLAN_SHORT[L][plan] },
    { sel: '[data-tour="plan-pill"]', t: "5. Your queries and plan", b: quotaLine(false, plan, remaining) },
  ];
}

const PLAN_LABEL: Record<WelcomePlan, string> = { lite: "Lite", basic: "Básico", pro: "Club Pro", advanced: "Avanzado" };
const PLAN_LABEL_EN: Record<WelcomePlan, string> = { lite: "Lite", basic: "Basic", pro: "Club Pro", advanced: "Advanced" };

/** Bienvenida con fondo oscuro, avatar grande y recorrido según el plan contratado. */
export default function WelcomeTour({ autoStart, onGoHome }: { autoStart: boolean; onGoHome?: () => void }) {
  const { lang } = useLanguage();
  const es = lang === "es";
  const me = useMyAssistant();
  const planUi = usePlanUi();
  const [open, setOpen] = useState(false);
  const [testPlan, setTestPlan] = useState<WelcomePlan | null>(null);
  const [step, setStep] = useState(-1); // -1 = bienvenida
  const [rect, setRect] = useState<DOMRect | null>(null);

  const plan = testPlan ?? welcomePlanFor(planUi.planKey);
  const name = me?.displayName ? `${me.displayName} IA` : es ? "tu Agente Retail IA" : "your Retail AI Agent";
  const remaining = testPlan ? null : planUi.quota?.limit != null ? planUi.quota.remaining : null;
  const steps = buildSteps(es, plan, name, remaining);
  const pose = me?.def ? assistantPose(me.def.id, "lean") ?? me.def.img : null;

  useEffect(() => {
    if (!autoStart) return;
    // Quita ?welcome=1 para que recargar no relance el tour.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has("welcome")) {
        params.delete("welcome");
        const q = params.toString();
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`);
      }
    } catch { /* ignore */ }
    try { if (localStorage.getItem(SEEN_KEY)) return; } catch { /* ignore */ }
    let cancelled = false;
    let id: number | undefined;
    void (async () => {
      // Marca guardada en la cuenta: solo se ve una vez en total, en cualquier dispositivo.
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (data.user?.user_metadata?.["welcome_tour_seen_at"]) {
        try { localStorage.setItem(SEEN_KEY, String(data.user.user_metadata["welcome_tour_seen_at"])); } catch { /* ignore */ }
        return;
      }
      id = window.setTimeout(() => { onGoHome?.(); setStep(-1); setOpen(true); }, 900);
    })();
    return () => { cancelled = true; if (id) window.clearTimeout(id); };
  }, [autoStart]);

  // Prueba de admin: primero lleva al Agente Retail y abre la bienvenida cuando su caja ya está cargada.
  const [pendingTick, setPendingTick] = useState(0);
  useEffect(() => {
    const h = (e: Event) => {
      const p = (e as CustomEvent<{ plan?: WelcomePlan }>).detail?.plan ?? "lite";
      pendingTestPlan = p;
      try { sessionStorage.setItem(PENDING_KEY, p); } catch { /* ignore */ }
      setOpen(false);
      onGoHome?.();
      setPendingTick((n) => n + 1);
    };
    window.addEventListener(START_WELCOME_TOUR_EVENT, h);
    return () => window.removeEventListener(START_WELCOME_TOUR_EVENT, h);
  }, [onGoHome]);

  useEffect(() => {
    let p = pendingTestPlan;
    if (!p) { try { p = (sessionStorage.getItem(PENDING_KEY) as WelcomePlan | null) ?? null; } catch { /* ignore */ } }
    if (!p) return;
    const plan = p;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (document.querySelector('[data-tour="sara-input"]') || tries > 40) {
        window.clearInterval(id);
        pendingTestPlan = null;
        try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
        setTestPlan(plan); setStep(-1); setOpen(true);
      }
    }, 150);
    return () => window.clearInterval(id);
  }, [pendingTick]);

  const measure = useCallback(() => {
    if (step < 0) { setRect(null); return; }
    const el = document.querySelector(steps[step]?.sel ?? "");
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setRect(el.getBoundingClientRect());
  }, [step, steps]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const t = window.setTimeout(measure, 350);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const close = () => {
    setOpen(false);
    if (testPlan) return; // la prueba de admin no marca la cuenta
    const at = new Date().toISOString();
    try { localStorage.setItem(SEEN_KEY, at); } catch { /* ignore */ }
    void supabase.auth.updateUser({ data: { welcome_tour_seen_at: at } });
  };

  if (!open || typeof document === "undefined") return null;

  const pad = 8;
  const hole = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(360, vw - 24);
  let cardStyle: React.CSSProperties = { left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: cardW };
  if (hole) {
    const below = hole.top + hole.height + 12;
    const top = below + 230 < vh ? below : Math.max(12, hole.top - 242);
    const left = Math.min(Math.max(12, hole.left), vw - cardW - 12);
    cardStyle = { top, left, width: cardW };
  }

  return createPortal(
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-label={es ? "Bienvenida al panel" : "Welcome to the panel"}>
      {hole ? (
        <div className="pointer-events-none absolute rounded-xl ring-2 ring-[#FFE143] transition-all duration-300"
          style={{ ...hole, boxShadow: "0 0 0 9999px hsl(var(--foreground) / 0.72)" }} />
      ) : (
        <div className="absolute inset-0 bg-foreground/75 backdrop-blur-sm" />
      )}

      {step < 0 ? (
        <div className="absolute left-1/2 top-1/2 w-[min(94vw,520px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl border border-border bg-card shadow-[0_30px_80px_-20px_hsl(var(--foreground)/0.45),0_8px_24px_-8px_hsl(var(--foreground)/0.2)]">
          <div className="relative bg-gradient-to-b from-[#FFE143]/35 via-[#FFE143]/10 to-card px-6 pb-2 pt-5 sm:px-8">
            <div className="flex items-center justify-between gap-2">
              <span className="font-display text-base font-bold tracking-tight leading-none text-foreground">
                RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
              </span>
              <span className="rounded-full border border-border bg-card px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {testPlan ? `${es ? "Prueba admin" : "Admin test"} · ` : ""}{es ? PLAN_LABEL[plan] : PLAN_LABEL_EN[plan]}
              </span>
            </div>
            <div className="relative mx-auto mt-2 flex h-48 items-end justify-center sm:h-60">
              <span className="absolute bottom-1 h-4 w-40 rounded-[50%] bg-foreground/10 blur-md" aria-hidden="true" />
              {pose ? <img src={pose} alt={name} className="relative h-full w-auto -scale-x-100 object-contain drop-shadow-xl" /> : me?.def ? <AssistantAvatar id={me.def.id} size={140} /> : (
                <span className="flex h-28 w-28 items-center justify-center rounded-full bg-[#FFE143] font-display text-3xl font-bold text-foreground">RL</span>
              )}
            </div>
          </div>
          <div className="px-6 pb-6 pt-3 text-center sm:px-8 sm:pb-8">
            <h2 className="font-display text-2xl font-bold text-foreground">{es ? `¡Hola! Soy ${name}` : `Hi! I'm ${name}`}</h2>
            <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">
              {es ? "Bienvenido a tu panel de Retail Lemon. En un minuto te enseño cómo está organizado y qué tienes con tu plan." : "Welcome to your Retail Lemon panel. In one minute I'll show you how it's organised and what your plan includes."}
            </p>
            <ul className="mx-auto mt-4 grid max-w-sm gap-2 text-left">
              {(es
                ? ["Pregúntame lo que necesites de tu negocio", "Pide ayuda con el «?» en cualquier página", "Navega por las herramientas de tu plan"]
                : ["Ask me anything about your business", "Get help with «?» on any page", "Browse the tools in your plan"]
              ).map((t) => (
                <li key={t} className="flex items-start gap-2 font-body text-sm text-foreground">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FFE143]"><Check className="h-3 w-3 text-foreground" strokeWidth={2.5} /></span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-col gap-2">
              <button type="button" onClick={() => setStep(0)} className="min-h-11 rounded-xl bg-foreground px-6 py-3 font-body text-sm font-semibold text-background">
                {es ? "Empezar tour (1 min)" : "Start tour (1 min)"}
              </button>
              <button type="button" onClick={close} className="min-h-11 font-body text-sm text-muted-foreground hover:text-foreground">
                {es ? "Explorar por mi cuenta" : "Explore on my own"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="absolute rounded-2xl border border-border bg-card p-5 shadow-2xl transition-all duration-300" style={cardStyle}>
          <button type="button" onClick={close} aria-label={es ? "Cerrar" : "Close"} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2 pr-8">
            {me?.def && <AssistantAvatar id={me.def.id} size={36} />}
            <h3 className="font-display text-base font-bold text-foreground">{steps[step]?.t}</h3>
          </div>
          <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">{steps[step]?.b}</p>
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="font-body text-xs tabular-nums text-muted-foreground">{step + 1}/{steps.length}</span>
            <div className="flex gap-2">
              {step > 0 && (
                <button type="button" onClick={() => setStep(step - 1)} aria-label={es ? "Anterior" : "Back"} className="flex h-11 w-11 items-center justify-center rounded-xl border border-border hover:bg-secondary">
                  <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                </button>
              )}
              <button type="button" onClick={() => (step < steps.length - 1 ? setStep(step + 1) : close())}
                className="flex min-h-11 items-center gap-1.5 rounded-xl bg-foreground px-4 font-body text-sm font-semibold text-background">
                {step < steps.length - 1 ? <>{es ? "Siguiente" : "Next"}<ArrowRight className="h-4 w-4" strokeWidth={1.5} /></> : <>{es ? "¡A trabajar!" : "Let's go!"}<Check className="h-4 w-4" strokeWidth={1.5} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
